import React, { useState, useEffect } from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import { Typography, Steps, Popover, Tag, Tooltip, Flex } from "antd";
import orderService from "../../services/OrderLists";
import { useParams } from "react-router-dom";
import moment from "moment";
import usePermissions from "../../hooks/usePermissions";
import { useMediaQuery } from "react-responsive";
import AssignTagsPopover from "../../components/OrderTags/AssignTagsPopover";
import orderTagService from "../../services/orderTagService";
const { Title } = Typography;

function FixedOrderDetails({ orderData, onTagsChanged }) {
  // console.log("orderData", orderData);
  const [statusHistory, setStatusHistory] = useState([]);
  const [currentStatus, setCurrentStatus] = useState({});
  const [timeInCurrentStatus, setTimeInCurrentStatus] = useState("");
  const [allTags, setAllTags] = useState([]);
  const { orderKey } = useParams();
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  useEffect(() => {
    orderTagService.getTags().then((res) => {
      if (res?.success) setAllTags(res.data || []);
    });
  }, []);

  useEffect(() => {
    let intervalId;

    const fetchAndSetStatus = async () => {
      if (!orderKey) return;

      try {
        const response = await orderService.getStatusLogs(orderKey);
        if (response.success) {
          const historyData = Array.isArray(response.data.data)
            ? response.data.data
            : [];
          setStatusHistory(historyData);

          const latestStatusIndex = historyData.reduce(
            (latestIndex, item, index) =>
              item.history_date ? index : latestIndex,
            -1
          );

          const newCurrentStatus =
            latestStatusIndex !== -1 ? historyData[latestStatusIndex] : {};

          setCurrentStatus(newCurrentStatus);

          if (newCurrentStatus?.history_date) {
            const updateTime = () => {
              const timePassed = calculateTimeInCurrentStatus(
                newCurrentStatus.history_date
              );
              setTimeInCurrentStatus(timePassed);
            };

            updateTime();
            intervalId = setInterval(updateTime, 60000);
          }
        }
      } catch (error) {
        console.error("Error fetching status logs:", error);
        setStatusHistory([]);
        setCurrentStatus({});
      }
    };

    fetchAndSetStatus();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [orderKey]);

  const calculateTimeInCurrentStatus = (statusDate) => {
    if (!statusDate) return "N/A";

    const now = moment();
    const lastStatusTime = moment(statusDate);
    const duration = moment.duration(now.diff(lastStatusTime));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    return `${hours > 0 ? `${hours} hr` : ""} ${minutes > 0 ? `${minutes} m` : "just now"
      }`;
  };

  const statusColors = {
    Unassigned: "gray",
    "Assigned to manager": "blue",
    "In Progress": "orange",
    "Quality Check": "purple",
    Completed: "cyan",
    Delivered: "green",
    Feedback: "blue",
    Rejected: "red",
    Cancelled: "gray",
    "Cancelled by User": "gray",
    Failed: "gray",
  };

  const paymentColors = {
    Full: "green",
    Partial: "yellow",
    Unpaid: "red",
  };

  const latestStatusIndex = statusHistory.reduce(
    (latestIndex, item, index) => (item.history_date ? index : latestIndex),
    -1
  );

  const items = statusHistory.map((item) => ({
    title: (
      <Tooltip
      // title={
      //   item.history_date
      //     ? `Date: ${moment(item.history_date)
      //         .local()
      //         .format("YYYY-MM-DD hh:mm A")}`
      //     : ""
      // }
      >
        <div style={{ fontSize: "14px" }}>
          {item.status}
          <p style={{ color: "gray", margin: 0, fontSize: "12px" }}>
            {item.history_date
              ? moment(item.history_date).local().format("DD-MM-YYYY hh:mm A")
              : "No date available"}
          </p>
        </div>
      </Tooltip>
    ),
    status: item.history_date ? "finish" : "wait",
    icon: item.history_date ? (
      <CheckCircleOutlined style={{ color: "#00C000" }} />
    ) : null,
    color: item.history_date ? "green" : "gray",
  }));

  const content = (
    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
      <Steps
        size="small"
        direction="vertical"
        current={latestStatusIndex}
        items={items}
      />
    </div>
  );

  return (
    <div style={{ padding: "10px" }}>
      <div
        style={{
          display: isMobile ? "grid" : "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          marginBottom: "15px",
        }}
      >
        <Title
          style={{
            color: "#001529",
            fontSize:
              orderData?.data?.title?.length > 50
                ? "clamp(12px, 3vw, 20px)" // smaller for long titles
                : "clamp(12px, 3vw, 25px)",  // normal for short titles
            margin: "0 20px 0 0",
            maxWidth: isMobile ? "100%" : "80%",
            wordBreak: "break-word",
            textAlign: "left",
          }}
        >
          {orderData?.data?.title}
        </Title>

        <div
          style={{
            fontSize: "16px",
            color: "black",
            fontWeight: "500",
            whiteSpace: "nowrap",
            minWidth: "150px",
            textAlign: isMobile ? "left" : "right",
            marginTop: isMobile ? "5px" : "0px"
          }}
        >
          Been this stage for{" "}
          <span style={{ color: "#001529", fontWeight: "700" }}>
            {timeInCurrentStatus}
          </span>
        </div>
      </div>
      <div className="scrollable-container">
        <div className="fixed-container">
          <Popover content={content} title="Order Status">
            <Tag
              style={{ fontWeight: 600 }}
              color={statusColors[currentStatus.status] || "default"}>
              {orderData?.data?.status}
            </Tag>
          </Popover>
          {orderData?.data?.mark_as_draft && (
            <Tag color="red" style={{ fontWeight: 600 }}>
            Draft Submission
            </Tag>
          )}
          {/* {orderData?.data?.has_locus_coin && (
            <Tag color="gold" style={{ fontWeight: 600 }}>
              Bonus Balance: {orderData?.data?.bonus_balance ?? 0}
            </Tag>
          )} */}
          {hasPermission("can_view_order_payment_details") && (
            <Tooltip
              placement="bottom"
              title={
                hasPermission("can_view_order_payment_details") &&
                  orderData?.data?.payment?.updated_at ? (
                  <div style={{ textAlign: "center", width: "100%" }}>
                    Last Updated :{" "}
                    {moment(orderData.data.payment.updated_at)
                      .local()
                      .format("DD-MM-YYYY hh:mm A")}
                  </div>
                ) : (
                  "Payment not updated"
                )
              }>
              <Tag
                style={{ fontWeight: 600 }}
                color={
                  paymentColors[orderData?.data?.payment?.payment_status] ||
                  "default"
                }>
                {orderData?.data?.payment?.payment_status || "Not Updated"}
              </Tag>
            </Tooltip>
          )}
          <Tooltip title="Indicates the situation provided by the sales team to the operations team, based on the client's past experience or a new request.">
            {orderData?.data?.salessituation ? (
              <Tag color="purple">
                {orderData?.data?.salessituation?.situation || "Not Updated"}
              </Tag>
            ) : (
              <span>Not Available</span> // Fallback when no data is present
            )}
          </Tooltip>
          <Tooltip title="Writer Status">
            {orderData?.data?.writer_status ? (
              <Tag color="orange" style={{ fontWeight: 600 }}>
                {orderData?.data?.writer_status}
              </Tag>
            ) : (
              <Tag color="orange" style={{ fontWeight: 600 }}>
                Not Updated
              </Tag>
            )}
          </Tooltip>
          <Tooltip title="Whether a Duplitext plagiarism search has been run for this order">
            <Tag
              color={orderData?.data?.has_duplitext_search ? "green" : "red"}
              style={{ fontWeight: 600 }}>
              Duplitext: {orderData?.data?.has_duplitext_search ? "Yes" : "No"}
            </Tag>
          </Tooltip>
          <div style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 4,
            borderLeft: "2px solid #e0e0e0",
            paddingLeft: 10,
            marginLeft: 4,
          }}>
            {orderData?.data?.tags && orderData.data.tags.length > 0
              ? orderData.data.tags.map((tag) => (
                  <Tag key={tag} color="purple" style={{ margin: 0 }}>
                    {tag}
                  </Tag>
                ))
              : <span style={{ color: "#9CA3AF", fontSize: 12 }}>No tags</span>
            }
            <AssignTagsPopover
              taskKey={orderKey}
              allTags={allTags}
              onTagsChanged={onTagsChanged}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default FixedOrderDetails;
